import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { TableColumn } from '../../../models/irembo-data-table.model';
import {
  TableWithInputColumn,
  TableWithInputColumnKind,
  TableWithInputRow,
  getTableWithInputOptionLabel,
  getTableWithInputOptionValue,
  resolveTableWithInputColumnKind,
  resolveTableWithInputSelectDisplayValues,
} from '../../../models/irembo-table-with-input.model';
import { ToastService } from '../../../services/toast/toast.service';

interface UniquenessError {
  key: string;
  message: string;
}

let rowIdCounter = 0;
const generateRowId = (): string =>
  `${Date.now().toString(36)}-${(rowIdCounter++).toString(36)}`;

const minItemsValidator = (getMinItems: () => number): ValidatorFn => {
  return control => {
    const minItems = getMinItems();
    if (!minItems) {
      return null;
    }
    const value = control.value;
    const length = Array.isArray(value) ? value.length : 0;
    return length < minItems ? { minItems: true } : null;
  };
};

@Component({
  selector: 'irembogov-custom-table-with-input',
  templateUrl: './custom-table-with-input.component.html',
  styleUrls: ['./custom-table-with-input.component.scss'],
})
export class CustomTableWithInputComponent
  extends FieldType<FieldTypeConfig>
  implements OnInit, OnDestroy
{
  @ViewChild('confirmModalTpl') confirmModalTpl!: TemplateRef<unknown>;

  rows: TableWithInputRow[] = [];
  selectedRows: TableWithInputRow[] = [];
  searchTerm = '';

  panelOpen = false;
  panelMode: 'add' | 'edit' = 'add';
  rowForm!: FormGroup;
  editingRowId: string | null = null;
  uniquenessErrors: UniquenessError[] = [];

  confirmTitle = '';
  confirmMessage = '';
  // Set alongside confirmMessage when a specific value (e.g. the row being
  // deleted) should render bold within the dialog copy instead of plain text.
  confirmMessagePrefix = '';
  confirmMessageValue: string | null = null;
  confirmMessageSuffix = '';
  confirmAcceptLabel = 'Confirm';
  confirmCancelLabel = 'Cancel';
  confirmVariant: 'danger' | 'warning' = 'danger';
  private confirmAction: (() => void) | null = null;

  // filteredRows is read every change-detection cycle via the [data]/
  // [collectionSize] template bindings (this component isn't OnPush). Without
  // caching, a truthy searchTerm makes it re-run .filter() and return a brand
  // new array on every single cycle — even when neither rows nor searchTerm
  // actually changed — which the child table treats as new data and resets
  // its current page for, silently kicking users back to page 1 while
  // searching. Cache by (rows reference, searchTerm) so it only recomputes
  // when one of those genuinely changes.
  private filteredRowsCache: TableWithInputRow[] | null = null;
  private filteredRowsCacheSource: TableWithInputRow[] | null = null;
  private filteredRowsCacheTerm = '';

  // commitRows() flips this on for the duration of its own setValue() call,
  // so the subscription below can tell that write apart from one arriving
  // from outside this component — e.g. draft-resume or RFA data arriving
  // asynchronously and getting patched into the form after this field
  // already initialized with an empty/stale value. commitRows() must still
  // let setValue() emit (formly's own FieldType subscribes to
  // control.valueChanges to keep the plain `model` object it hands back on
  // submit in sync — emitEvent:false would suppress that too, and every
  // consumer that submits `model` directly, not `form.value`, would send a
  // stale/default value for this field no matter what the user entered).
  private isCommittingRows = false;
  private valueChangesSubscription: Subscription | null = null;

  constructor(
    private readonly fb: FormBuilder,
    private readonly modalService: NgbModal,
    private readonly toastService: ToastService,
    private readonly translate: TranslateService
  ) {
    super();
  }

  ngOnInit(): void {
    this.syncRowsFromControlValue(this.formControl.value);

    // The form's model can be populated after this field has already
    // initialized — e.g. saved draft/RFA data fetched asynchronously and
    // patched into the reactive form once it arrives. Without this, `rows`
    // (and therefore the rendered table) stays frozen at whatever value was
    // present at construction time, typically empty.
    this.valueChangesSubscription = this.formControl.valueChanges.subscribe(
      value => {
        if (this.isCommittingRows) {
          return;
        }
        this.syncRowsFromControlValue(value);
      }
    );

    this.formControl.addValidators(minItemsValidator(() => this.minItems));
    if (this.minItems) {
      this.field.validation ??= {};
      this.field.validation.messages = {
        ...this.field.validation.messages,
        minItems:
          this.field.validation?.messages?.['minItems'] ||
          `${this.translate.instant('Minimum of')} ${this.minItems} ${
            this.minItems === 1
              ? this.translate.instant('item required.')
              : this.translate.instant('items required.')
          }`,
      };
    }
    this.formControl.updateValueAndValidity();
  }

  ngOnDestroy(): void {
    this.valueChangesSubscription?.unsubscribe();
  }

  // Persisted/preloaded values are plain application data and may not carry
  // our internal __rowId — backfill it here so every row has a stable id to
  // key edit/delete/selection off, and never let it leak back out (see
  // commitRows).
  private syncRowsFromControlValue(value: unknown): void {
    const incomingRows: Record<string, unknown>[] = Array.isArray(value)
      ? value
      : [];
    this.rows = incomingRows.map(row =>
      row['__rowId']
        ? (row as TableWithInputRow)
        : ({ ...row, __rowId: generateRowId() } as TableWithInputRow)
    );
  }

  get columns(): TableWithInputColumn[] {
    return this.field.props?.['columns'] || [];
  }

  get tableColumns(): TableColumn[] {
    return this.columns
      .filter(column => !column.props?.hiddenColumn)
      .map(column => ({
        key: column.key,
        title: column.props?.label ?? column.key,
        width: column.props?.widthPercent
          ? `${column.props.widthPercent}%`
          : undefined,
      }));
  }

  // My row template always adds one more <td> than `tableColumns` for the
  // edit/delete action icons, but the shared table's own header row is built
  // from whatever is bound to [columns] — with no matching blank <th> for
  // that extra column, the header row (and its background) falls short of
  // the actions column's width. Bound to [columns] instead of tableColumns
  // (header/column-count purposes only — my row template still iterates
  // tableColumns directly for its data cells, so this doesn't add a stray
  // data cell).
  //
  // Memoized: this is bound straight to the shared table's [columns] input,
  // which is read on every change-detection cycle (this component isn't
  // OnPush). Building a new array on every call — regardless of whether
  // anything actually changed — made the child table treat [columns] as
  // "new data" on every unrelated CD tick anywhere in the app, silently
  // resetting its current page back to 1. Cache by the inputs that actually
  // determine the result.
  private headerColumnsCache: TableColumn[] | null = null;
  private headerColumnsCacheKey: string | null = null;

  get headerColumns(): TableColumn[] {
    const key = JSON.stringify([
      this.columns,
      this.isReadOnly,
      this.enableEdit,
      this.enableDeletion,
    ]);
    if (this.headerColumnsCache && this.headerColumnsCacheKey === key) {
      return this.headerColumnsCache;
    }
    this.headerColumnsCache =
      this.isReadOnly || (!this.enableEdit && !this.enableDeletion)
        ? this.tableColumns
        : [
            ...this.tableColumns,
            // A soft target, not a hard floor — the table must always fit
            // its container without scrolling, so this column has to be
            // able to shrink like any other. The edit/delete buttons wrap
            // onto their own line when there isn't room side by side (see
            // the actions <td> in the template, which isn't nowrap) instead
            // of a fixed minimum width forcing the whole table wider than
            // its container.
            { key: '__actions', title: '', width: '25%' },
          ];
    this.headerColumnsCacheKey = key;
    return this.headerColumnsCache;
  }

  // Application review/preview pages (UC-OFF-07/UC-CIT-07) render this field
  // with its control disabled — in that context every interactive affordance
  // (search, row selection, Add Item, edit/delete icons) should disappear
  // entirely rather than just render disabled, leaving a plain read-only
  // table + item count.
  get isReadOnly(): boolean {
    return this.formControl.disabled;
  }

  get filteredRows(): TableWithInputRow[] {
    if (!this.showSearch || !this.searchTerm.trim()) {
      return this.rows;
    }
    if (
      this.filteredRowsCache &&
      this.filteredRowsCacheSource === this.rows &&
      this.filteredRowsCacheTerm === this.searchTerm
    ) {
      return this.filteredRowsCache;
    }
    const term = this.searchTerm.trim().toLowerCase();
    // Search against the same formatted/translated value the table
    // displays (e.g. a resolved dropdown label, Yes/No for a checkbox) so
    // matches line up with what the user can actually see, not the raw
    // stored value underneath it.
    this.filteredRowsCache = this.rows.filter(row =>
      this.tableColumns.some(column =>
        this.formatCellValue(row, column).toLowerCase().includes(term)
      )
    );
    this.filteredRowsCacheSource = this.rows;
    this.filteredRowsCacheTerm = this.searchTerm;
    return this.filteredRowsCache;
  }

  get enableEdit(): boolean {
    return this.field.props?.['enableEdit'] ?? true;
  }

  get enableDeletion(): boolean {
    return this.field.props?.['enableDeletion'] ?? true;
  }

  get enableRowSelection(): boolean {
    return this.field.props?.['enableRowSelection'] ?? false;
  }

  get showSearch(): boolean {
    return this.field.props?.['showSearch'] ?? false;
  }

  get showPagination(): boolean {
    return this.field.props?.['showPagination'] ?? true;
  }

  get itemsPerPage(): number {
    return this.field.props?.['itemsPerPage'] ?? 10;
  }

  get maxItems(): number | null {
    return this.field.props?.['maxItems'] ?? null;
  }

  // Only fall back to a minimum of 1 when the SM has explicitly marked the
  // whole table required — an untouched/optional table must stay optional
  // rather than silently requiring at least one row.
  get minItems(): number {
    const configured = this.field.props?.['minItems'];
    if (configured !== undefined && configured !== null) {
      return configured;
    }
    return this.field.props?.required ? 1 : 0;
  }

  get isMaxItemsReached(): boolean {
    return !!this.maxItems && this.rows.length >= this.maxItems;
  }

  columnKind(column: TableWithInputColumn): TableWithInputColumnKind {
    return resolveTableWithInputColumnKind(column);
  }

  // Honor a select column's configured bindValue/bindLabel (instead of
  // always assuming the option shape is exactly {label, value}) when
  // rendering <option> elements in the add/edit panel.
  optionValue(
    column: TableWithInputColumn,
    option: Record<string, unknown>
  ): unknown {
    return getTableWithInputOptionValue(column, option);
  }

  optionLabel(
    column: TableWithInputColumn,
    option: Record<string, unknown>
  ): string {
    return getTableWithInputOptionLabel(column, option);
  }

  formatCellValue(row: TableWithInputRow, column: TableColumn): string {
    const value = (row as Record<string, unknown>)[column.key];
    const columnConfig = this.columns.find(c => c.key === column.key);
    if (columnConfig) {
      const kind = resolveTableWithInputColumnKind(columnConfig);
      if (kind === 'checkbox') {
        return this.translate.instant(value ? 'Yes' : 'No');
      }
      if (kind === 'select') {
        return resolveTableWithInputSelectDisplayValues(columnConfig, value)
          .map(label => this.translate.instant(label))
          .join(', ');
      }
    }
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  }

  requiredErrorMessage(column: TableWithInputColumn): string {
    return column.validation?.messages?.required || 'This field is required';
  }

  private buildRowForm(initial?: TableWithInputRow): FormGroup {
    const group: Record<string, unknown> = {};
    this.columns.forEach(column => {
      const validators = [];
      if (column.props?.required) {
        validators.push(Validators.required);
      }
      if (resolveTableWithInputColumnKind(column) === 'email') {
        validators.push(Validators.email);
      }
      const kind = resolveTableWithInputColumnKind(column);
      let defaultValue: unknown = null;
      if (kind === 'checkbox') {
        defaultValue = column.props?.default ?? false;
      } else if (kind === 'select' && column.props?.multiple) {
        defaultValue = [];
      }
      const initialValue = initial
        ? (initial as Record<string, unknown>)[column.key] ?? defaultValue
        : defaultValue;
      group[column.key] = [
        { value: initialValue, disabled: !!column.props?.disabled },
        validators,
      ];
    });
    return this.fb.group(group);
  }

  openAddPanel(): void {
    if (this.isMaxItemsReached || this.formControl.disabled) {
      return;
    }
    this.panelMode = 'add';
    this.editingRowId = null;
    this.uniquenessErrors = [];
    this.rowForm = this.buildRowForm();
    this.panelOpen = true;
  }

  openEditPanel(row: TableWithInputRow): void {
    if (!this.enableEdit || this.formControl.disabled) {
      return;
    }
    this.panelMode = 'edit';
    this.editingRowId = row.__rowId;
    this.uniquenessErrors = [];
    this.rowForm = this.buildRowForm(row);
    this.rowForm.markAsPristine();
    this.panelOpen = true;
  }

  closePanel(): void {
    this.panelOpen = false;
    this.editingRowId = null;
    this.uniquenessErrors = [];
  }

  cancelPanel(): void {
    if (this.rowForm?.dirty) {
      this.confirmTitle = this.translate.instant(
        this.panelMode === 'add' ? 'Discard new item' : 'Discard Changes'
      );
      this.confirmMessage = this.translate.instant(
        this.panelMode === 'add'
          ? 'You have unsaved changes. Do you want to discard them? This action cannot be reverted'
          : 'All changes made on this item will be lost. This action cannot be reverted'
      );
      this.confirmMessageValue = null;
      this.confirmAcceptLabel = this.translate.instant('Discard');
      this.confirmCancelLabel = this.translate.instant('Continue Editing');
      this.confirmVariant = 'warning';
      this.confirmAction = () => this.closePanel();
      this.modalService.open(this.confirmModalTpl, {
        centered: true,
        windowClass: 'table-with-input-confirm-modal',
      });
    } else {
      this.closePanel();
    }
  }

  private checkUniqueness(values: Record<string, unknown>): UniquenessError[] {
    return this.columns
      .filter(column => column.props?.unique)
      .map(column => {
        const value = values[column.key];
        const isDuplicate =
          value !== null &&
          value !== undefined &&
          value !== '' &&
          this.rows.some(
            row =>
              row.__rowId !== this.editingRowId &&
              (row as Record<string, unknown>)[column.key] === value
          );
        return isDuplicate
          ? {
              key: column.key,
              message:
                column.validation?.messages?.unique ||
                'This value already exists',
            }
          : null;
      })
      .filter((error): error is UniquenessError => !!error);
  }

  getUniquenessError(key: string): string | undefined {
    return this.uniquenessErrors.find(error => error.key === key)?.message;
  }

  private commitRows(): void {
    // __rowId is component-only metadata for keying UI actions — strip it so
    // it never gets written into the submitted application data.
    const values = this.rows.map(({ __rowId, ...rest }) => rest);
    // `rows` is already up to date (we're writing it, not reading it back),
    // so the ngOnInit subscription skips syncing while isCommittingRows is
    // set — otherwise it would treat this self-write as an external patch
    // and rebuild `rows` from the stripped `values`, regenerating every
    // __rowId and desyncing anything keyed off the old ones (e.g.
    // selectedRows). setValue must still emit (see isCommittingRows above):
    // formly's own FieldType subscribes to this control's valueChanges to
    // keep the plain `model` object in sync, and consumers that submit
    // `model` directly rely on that to see this field's actual value.
    this.isCommittingRows = true;
    this.formControl.setValue(values);
    this.isCommittingRows = false;
    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
  }

  save(): boolean {
    if (this.rowForm.invalid) {
      this.rowForm.markAllAsTouched();
      return false;
    }

    const values = this.rowForm.getRawValue();
    const uniquenessErrors = this.checkUniqueness(values);
    if (uniquenessErrors.length) {
      this.uniquenessErrors = uniquenessErrors;
      this.toastService.show({
        title: this.translate.instant('Duplicate value'),
        body: this.translate.instant(uniquenessErrors[0].message),
        type: 'error',
      });
      return false;
    }

    if (this.panelMode === 'add') {
      const newRow: TableWithInputRow = {
        ...values,
        __rowId: generateRowId(),
      };
      this.rows = [...this.rows, newRow];
    } else {
      this.rows = this.rows.map(row =>
        row.__rowId === this.editingRowId ? { ...row, ...values } : row
      );
    }
    this.commitRows();
    return true;
  }

  onSave(): void {
    if (this.save()) {
      this.closePanel();
    }
  }

  deleteRow(row: TableWithInputRow): void {
    if (!this.enableDeletion || this.formControl.disabled) {
      return;
    }
    const firstColumn = this.columns[0];
    const displayValue = firstColumn
      ? (row as Record<string, unknown>)[firstColumn.key]
      : '';
    this.confirmTitle = this.translate.instant('Delete Item');
    this.confirmMessagePrefix = `${this.translate.instant(
      'Do you want to delete'
    )} "`;
    this.confirmMessageValue = String(displayValue);
    this.confirmMessageSuffix = `" ${this.translate.instant(
      'from the list. This action cannot be reverted'
    )}`;
    this.confirmAcceptLabel = this.translate.instant('Delete');
    this.confirmCancelLabel = this.translate.instant('Cancel');
    this.confirmVariant = 'danger';
    this.confirmAction = () => {
      this.rows = this.rows.filter(r => r.__rowId !== row.__rowId);
      this.selectedRows = this.selectedRows.filter(
        r => r.__rowId !== row.__rowId
      );
      this.commitRows();
    };
    this.modalService.open(this.confirmModalTpl, {
      centered: true,
      windowClass: 'table-with-input-confirm-modal',
    });
  }

  onSelectionChanged(selected: TableWithInputRow[]): void {
    this.selectedRows = this.enableRowSelection ? selected : [];
  }

  bulkDelete(): void {
    if (!this.enableRowSelection || !this.selectedRows.length) {
      return;
    }
    this.confirmTitle = this.translate.instant('Delete Item');
    this.confirmMessage = `${this.translate.instant('Do you want to delete')} ${
      this.selectedRows.length
    } ${
      this.selectedRows.length === 1
        ? this.translate.instant(
            'selected item from the list? This action cannot be reverted'
          )
        : this.translate.instant(
            'selected items from the list? This action cannot be reverted'
          )
    }`;
    this.confirmMessageValue = null;
    this.confirmAcceptLabel = this.translate.instant('Delete');
    this.confirmCancelLabel = this.translate.instant('Cancel');
    this.confirmVariant = 'danger';
    this.confirmAction = () => {
      const idsToRemove = new Set(this.selectedRows.map(row => row.__rowId));
      this.rows = this.rows.filter(row => !idsToRemove.has(row.__rowId));
      this.selectedRows = [];
      this.commitRows();
    };
    this.modalService.open(this.confirmModalTpl, {
      centered: true,
      windowClass: 'table-with-input-confirm-modal',
    });
  }

  onConfirmAccept(): void {
    this.confirmAction?.();
    this.confirmAction = null;
  }
}
